/**
 * Game content shared by client and server: drinks and the
 * numbers that drive the Kaki Dollar economy. The server enforces every rule
 * here; the client only reads it to preview what an action will do.
 *
 * Furniture lives in ./furniture, split into one section per feature.
 */

export * from './furniture/index.js';

/* -------------------------------------------------------------------------- */
/* Drinks                                                                     */
/* -------------------------------------------------------------------------- */

export const DRINK_KINDS = ['kopi', 'kopi_o', 'kopi_c', 'teh_tarik', 'milo', 'bandung'] as const;
export type DrinkKind = (typeof DRINK_KINDS)[number];

export const DRINKS: Readonly<Record<DrinkKind, { label: string; description: string }>> =
  Object.freeze({
    kopi: { label: 'Kopi', description: 'Coffee with condensed milk.' },
    kopi_o: { label: 'Kopi O', description: 'Black coffee with sugar.' },
    kopi_c: { label: 'Kopi C', description: 'Coffee with evaporated milk.' },
    teh_tarik: { label: 'Teh Tarik', description: 'Pulled milk tea, extra foam.' },
    milo: { label: 'Milo', description: 'Chocolate malt, a childhood classic.' },
    bandung: { label: 'Bandung', description: 'Rose syrup and milk. Very pink.' },
  });

/** How long a drink stays in hand before it is finished. */
export const DRINK_DURATION_MS = 120_000;

/* -------------------------------------------------------------------------- */
/* Economy: Kaki Dollars (K$)                                                 */
/* -------------------------------------------------------------------------- */

export const CURRENCY_NAME = 'Kaki Dollars';
export const CURRENCY_SYMBOL = 'K$';

/** Whole Kaki Dollars; there are no cents. */
export function formatMoney(amount: number): string {
  const sign = amount < 0 ? '-' : '';
  return `${sign}${CURRENCY_SYMBOL}${Math.abs(Math.trunc(amount)).toLocaleString('en-SG')}`;
}

export const STARTING_MONEY = 10_000;
/**
 * Daily check-in: the reward grows with each consecutive Singapore day claimed,
 * and every 7th day pays a bonus on top. Missing a day starts the streak over.
 */
export const DAILY_BONUS_MONEY = 500;
export const DAILY_BONUS_STEP = 100;
/** The streak length after which the daily reward stops growing. */
export const DAILY_BONUS_MAX_STREAK = 10;
export const DAILY_STREAK_MILESTONE = 7;
export const DAILY_STREAK_MILESTONE_BONUS = 1_000;

/** What the check-in pays on the given day of a streak (1 = first day). */
export function dailyBonusFor(streak: number): number {
  const day = Math.max(1, Math.floor(streak));
  const base = DAILY_BONUS_MONEY + DAILY_BONUS_STEP * (Math.min(day, DAILY_BONUS_MAX_STREAK) - 1);
  return day % DAILY_STREAK_MILESTONE === 0 ? base + DAILY_STREAK_MILESTONE_BONUS : base;
}

/** The game's "day" turns over at midnight Singapore time (UTC+8, no daylight saving). */
const SG_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Whole Singapore days since the epoch. */
export function sgDay(ms: number): number {
  return Math.floor((ms + SG_OFFSET_MS) / DAY_MS);
}
/** Start (UTC ms) of the Singapore day containing `ms`. */
export function sgDayStart(ms: number): number {
  return sgDay(ms) * DAY_MS - SG_OFFSET_MS;
}

/**
 * Playtime pay: every 30 minutes connected in a Singapore day (adding up
 * across visits) earns a payout the player collects. There is no daily limit: staying longer keeps paying.
 * Registered players only.
 */
export const PLAYTIME_BLOCK_MS = 30 * 60 * 1000;
export const PLAYTIME_BLOCK_MONEY = 150;
/** How often the server adds up time for connected players. */
export const PLAYTIME_TICK_MS = 60 * 1000;
/** Upper bound on any single balance, far beyond normal play; guards overflow bugs. */
export const MAX_BALANCE = 1_000_000_000;

/** Respects each player may give per day (at most one per recipient). */
export const DAILY_RESPECTS = 3;

export const MAX_MOTTO_LENGTH = 60;
export const MAX_ROOM_NAME_LENGTH = 32;
export const MAX_ROOM_DESCRIPTION_LENGTH = 140;
